import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { BasicButton } from 'routes/staking_v2/components/BasicButton';

import CircleSpinner from 'components/circleSpinner/CircleSpinner';

import { useDailyRewardsCalendarQuery } from '../hooks/useDailyRewardsCalendarQuery';
import { queryKeys } from 'hooks/queries/query-keys';
import { useCountdown } from 'hooks/useCountdown';
import { useScreenSize } from 'hooks/useScreenSize';
import { useWindowSize } from 'hooks/useWindowSize';
import { IState } from 'models/reducers/i-state.interface';
import { useSelector } from 'react-redux';
import { EligibilityCheck } from '../dailyRewards.models';
import { formatRMVValue } from 'utils/number-format.utils';

import chestDesktop from 'assets/daily-rewards/chest_opening_desktop.mp4';
import chestMobile from 'assets/daily-rewards/chest_opening_mobile.mp4';

import {
  DESKTOP_LAYOUT_BREAKPOINT,
  getCalendarColumns,
  getDesktopCalendarColumns,
  GRID_COLS_CLASS,
} from '../constants';
import { DailyRewardsModal } from '../DailyRewardsModal';
import { CalendarDay } from './CalendarDay';
import styles from './CalendarModal.module.scss';
import { ClaimRewardButton } from './ClaimRewardButton';

const GRADIENT_STYLE_TOP = { background: 'linear-gradient(to bottom, #101010 0%, rgba(16, 16, 16, 0) 100%)' };
const GRADIENT_STYLE_BOTTOM = { background: 'linear-gradient(to bottom, rgba(16, 16, 16, 0) 0%, #101010 100%)' };

type CalendarModalProps = {
  onClose: () => void;
  onClaimReward: () => void;
  onRewardHistory: () => void;
  isClaimPending?: boolean;
  claimCooldownUntil?: number | null;
  year?: number;
  month?: number;
  isCurrentMonth?: boolean;
};

const formatAmount = (value: string) => formatRMVValue(Number(value || '0'));

const gateLabel = (gate: EligibilityCheck['gate']) => (gate === 'rmv_balance' ? 'RMV' : 'token worth');

const buildEligibilityTooltip = (checks: EligibilityCheck[]): string | null => {
  const failed = checks.find((c) => !c.met);
  if (!failed) return null;
  return `You have ${formatAmount(failed.actual)} ${gateLabel(failed.gate)}, need ${formatAmount(failed.required)}.`;
};

export const CalendarModal = ({
  onClose,
  onClaimReward,
  onRewardHistory,
  isClaimPending,
  claimCooldownUntil,
  year,
  month,
  isCurrentMonth = true,
}: CalendarModalProps) => {
  const queryClient = useQueryClient();
  const screenSize = useScreenSize();
  const [windowWidth] = useWindowSize();
  const isDesktopLayout = windowWidth >= DESKTOP_LAYOUT_BREAKPOINT;
  const columns = isDesktopLayout ? getDesktopCalendarColumns(windowWidth) : getCalendarColumns(screenSize);
  const { calendarDays, claimAvailable, nextClaimDate, userEligibility, isLoading, error } =
    useDailyRewardsCalendarQuery(year, month, columns);
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const isWalletConnected = !!walletAddress;

  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const checkScroll = useCallback(() => {
    const mobileEl = mobileScrollRef.current;
    const desktopEl = desktopScrollRef.current;
    const el = mobileEl && mobileEl.scrollHeight > mobileEl.clientHeight + 1 ? mobileEl : desktopEl;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    checkScroll();
    const mobileEl = mobileScrollRef.current;
    const desktopEl = desktopScrollRef.current;
    mobileEl?.addEventListener('scroll', checkScroll);
    desktopEl?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      mobileEl?.removeEventListener('scroll', checkScroll);
      desktopEl?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll, calendarDays]);

  const handleCountdownFinished = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.dailyRewards.all });
  }, [queryClient]);
  const countdown = useCountdown(!claimAvailable ? nextClaimDate : null, handleCountdownFinished);
  const countdownText = countdown.isFinished
    ? ''
    : `Next reward will be available to claim in ${countdown.hours} hours and ${countdown.minutes} minutes`;

  const cooldownTargetIso = useMemo(
    () => (claimCooldownUntil && claimCooldownUntil > Date.now() ? new Date(claimCooldownUntil).toISOString() : null),
    [claimCooldownUntil]
  );
  const cooldown = useCountdown(cooldownTargetIso);
  const isInCooldown = !!cooldownTargetIso && !cooldown.isFinished;
  const cooldownText = isInCooldown ? `Please wait ${cooldown.seconds + cooldown.minutes * 60}s before trying again.` : '';

  // userEligibility is null only for anonymous requests per backend contract.
  // When wallet is connected but userEligibility is still null, the fetch is in flight —
  // don't assume eligible, otherwise the button flashes claimable on hard refresh.
  const isEligible = !!userEligibility?.eligible;
  const eligibilityTooltip = userEligibility ? buildEligibilityTooltip(userEligibility.checks) : null;

  const claimDisabledByTime = !claimAvailable && !countdown.isFinished;
  const claimDisabled = isLoading || claimDisabledByTime || !!isClaimPending || isInCooldown || !isEligible;

  let tooltipText = countdownText;
  if (!isWalletConnected) {
    tooltipText = 'Connect your wallet to claim daily rewards.';
  } else if (isInCooldown) {
    tooltipText = cooldownText;
  } else if (!isEligible && eligibilityTooltip) {
    tooltipText = eligibilityTooltip;
  }

  const actionButtons = (
    <>
      {isCurrentMonth && (
        <ClaimRewardButton onClick={onClaimReward} disabled={claimDisabled} countdownText={countdownText} tooltipText={tooltipText} />
      )}
      {isWalletConnected && (
        <BasicButton
          text="Reward History"
          onClick={onRewardHistory}
          disabled={!!isClaimPending}
          className="tw-h-[36px] tw-w-[128px] tw-px-[0px]"
          textClassName="tw-text-[14px]"
        />
      )}
    </>
  );

  return (
    <DailyRewardsModal
      onClose={onClose}
      closeButtonPosition={isDesktopLayout ? 'top-left' : 'top-right'}
      className="tw-flex tw-max-h-[calc(100dvh-94px)] tw-max-w-[350px] tw-flex-col min-[576px]:tw-max-h-[calc(100dvh-102px)] min-[836px]:tw-max-h-[610px] min-[836px]:tw-max-w-[1200px]"
    >
      <div className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
        {/* Mobile/tablet scroll fade — top */}
        <div
          className={twMerge(
            'tw-pointer-events-none tw-absolute tw-inset-x-[1px] tw-top-[1px] tw-z-10 tw-h-[162px] tw-transition-opacity tw-duration-300 min-[836px]:tw-hidden',
            canScrollUp ? 'tw-opacity-100' : 'tw-opacity-0'
          )}
          style={GRADIENT_STYLE_TOP}
        />

        {/* Scrollable area: sidebar + grid on mobile, row layout on desktop */}
        <div
          ref={mobileScrollRef}
          className={twMerge(
            'tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-pb-[50px] min-[836px]:tw-flex-row min-[836px]:tw-overflow-hidden min-[836px]:tw-pb-0',
            styles.hideScrollbar
          )}
        >
          <div className="tw-relative tw-flex tw-h-[294px] tw-w-[350px] tw-shrink-0 tw-flex-col tw-items-center tw-justify-between tw-gap-[20px] tw-overflow-hidden tw-px-[20px] tw-py-[30px] tw-font-jost min-[836px]:tw-h-auto min-[836px]:tw-w-[400px] min-[836px]:tw-px-[40px] min-[836px]:tw-pb-[50px] min-[836px]:tw-pt-[80px]">
            <div className="tw-pointer-events-none tw-absolute tw-inset-[1px] tw-overflow-hidden tw-rounded-t-[8px] lg:tw-rounded-l-[8.5px]">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="tw-h-full tw-w-full tw-object-cover"
                src={isDesktopLayout ? chestDesktop : chestMobile}
              />
            </div>
            <div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-gap-[10px] tw-text-white min-[836px]:tw-gap-[20px]">
              <h2 className="tw-text-[24px] tw-font-bold min-[836px]:tw-text-[40px]">Daily Rewards</h2>
              <p className="tw-text-[12px] tw-font-medium min-[836px]:tw-text-[18px]">
                Come back daily and collect
                <br />
                awesome rewards!
              </p>
            </div>
            {/* Desktop buttons */}
            <div className="tw-relative tw-z-10 tw-hidden tw-items-center tw-justify-center tw-gap-[20px] min-[836px]:tw-flex">
              {actionButtons}
            </div>
          </div>
          {isLoading ? (
            <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-py-[40px]">
              <CircleSpinner size={48} />
            </div>
          ) : error ? (
            <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-py-[40px] tw-text-[14px] tw-text-white/60">
              Failed to load calendar. Please try again.
            </div>
          ) : (
            <div className="tw-relative tw-flex tw-flex-col min-[836px]:tw-min-h-0 min-[836px]:tw-flex-1">
              {/* Desktop scroll fade — top */}
              <div
                className={twMerge(
                  'tw-pointer-events-none tw-absolute tw-inset-x-[1px] tw-top-[1px] tw-z-10 tw-hidden tw-h-[162px] tw-transition-opacity tw-duration-300 min-[836px]:tw-block',
                  canScrollUp ? 'tw-opacity-100' : 'tw-opacity-0'
                )}
                style={GRADIENT_STYLE_TOP}
              />
              <div
                ref={desktopScrollRef}
                className={twMerge(
                  'tw-grid tw-content-start tw-gap-[10px] tw-px-[15px] tw-py-[15px] min-[836px]:tw-min-h-0 min-[836px]:tw-gap-[20px] min-[836px]:tw-overflow-y-auto min-[836px]:tw-px-[20px] min-[836px]:tw-py-[20px]',
                  GRID_COLS_CLASS[columns],
                  styles.hideScrollbar
                )}
              >
                {calendarDays.map((item) => (
                  <CalendarDay
                    key={item.key}
                    label={item.label}
                    variant={item.variant}
                    rewardImage={item.rewardImage}
                    rewardLabel={item.rewardLabel}
                    vipMultiplier={item.vipMultiplier}
                  />
                ))}
              </div>
              {/* Desktop scroll fade — bottom */}
              <div
                className={twMerge(
                  'tw-pointer-events-none tw-absolute tw-inset-x-[1px] tw-bottom-[1px] tw-z-10 tw-hidden tw-h-[162px] tw-transition-opacity tw-duration-300 min-[836px]:tw-block',
                  canScrollDown ? 'tw-opacity-100' : 'tw-opacity-0'
                )}
                style={GRADIENT_STYLE_BOTTOM}
              />
            </div>
          )}
        </div>

        {/* Mobile/tablet scroll fade — bottom */}
        <div
          className={twMerge(
            'tw-pointer-events-none tw-absolute tw-inset-x-[1px] tw-bottom-[1px] tw-z-10 tw-h-[162px] tw-transition-opacity tw-duration-300 min-[836px]:tw-hidden',
            canScrollDown ? 'tw-opacity-100' : 'tw-opacity-0'
          )}
          style={GRADIENT_STYLE_BOTTOM}
        />

        {/* Mobile/tablet overlay buttons */}
        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-z-20 tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-[20px] tw-px-[20px] tw-py-[15px] min-[836px]:tw-hidden">
          {actionButtons}
        </div>
      </div>
    </DailyRewardsModal>
  );
};
